import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Shared LinkedIn OAuth callback page.
 *
 * Both User and Company login pages redirect here after LinkedIn auth.
 * Which one initiated the flow is stored in sessionStorage as
 * 'linkedin_login_flow' = 'user' | 'company' so we know which
 * AuthContext setter to call and where to navigate after success.
 *
 * Lives in Company/Login/ for historical reasons but serves both flows.
 */
function LinkedInCallback() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { loginUser, loginCompany } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const linkedinError = params.get('error');
        const linkedinErrorDesc = params.get('error_description');

        // LinkedIn returned an error instead of a code (e.g. user denied,
        // or app not configured correctly).
        if (linkedinError) {
          throw new Error(linkedinErrorDesc || linkedinError);
        }

        // CSRF check — state we sent must match what came back.
        const savedState = sessionStorage.getItem('linkedin_oauth_state');
        if (!savedState || savedState !== state) {
          throw new Error('Invalid state parameter (possible CSRF)');
        }
        sessionStorage.removeItem('linkedin_oauth_state');

        if (!code) {
          throw new Error('Authorization code not found');
        }

        // Which login flow started this? Defaults to 'user' if missing.
        const flow = sessionStorage.getItem('linkedin_login_flow') || 'user';
        sessionStorage.removeItem('linkedin_login_flow');

        const response = await fetch('/api/auth/linkedin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
          credentials: 'include',
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Authentication failed (${response.status})`);
        }

        const data = await response.json();

        if (flow === 'company') {
          loginCompany(data.email);
          navigate('/companyhome');
        } else {
          loginUser(data.email);
          navigate('/user-home');
        }
      } catch (err) {
        console.error('LinkedIn authentication error:', err);
        setError(err.message || 'Failed to log in with LinkedIn');
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [location, navigate, loginUser, loginCompany]);

  if (loading) {
    return (
      <div className="linkedin-callback-container" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="spinner">Processing LinkedIn login…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="linkedin-callback-container error" style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/login')} style={{ marginTop: 12 }}>
          Return to Login
        </button>
      </div>
    );
  }

  return null;
}

export default LinkedInCallback;
